/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * WebSocket message types and type guards.
 *
 * This file contains only type definitions and type guards for WebSocket messages.
 * For actual WebSocket hooks, use:
 * - useWebSocketContext() from '@/context/websocket-context' for raw access
 * - useSmartColumnWebSocket() from '@/hooks/use-unified-websocket.hook' for smart columns
 * - useEnrichmentWebSocket() from '@/hooks/use-unified-websocket.hook' for enrichment
 * - useLeadsWebSocket() from '@/hooks/use-unified-websocket.hook' for leads
 */

import type { EmailAttemptOutcome } from '@/types';
import type { EnrichmentFailureKind } from '@/containers/lists/$listId/invalid-email-retry';

export interface SmartColumnMessage {
  type: 'smart_column_update';
  task_id: string;
  lead_list_id?: string;
  column_type: string;
  status: string;
  progress: {
    total?: number;
    processed?: number;
    failed?: number;
    percentage?: number;
    error?: string;
  };
  column_name?: string;
  timestamp: string;
}

export interface SmartColumnLeadUpdateMessage {
  type: 'smart_column_lead_update';
  task_id: string;
  lead_id: string;
  column_type: string;
  column_name?: string;
  result: {
    id: string | null;
    lead_id: string;
    lead_list_id: string;
    processed_value?: any;
    original_value?: any;
    status: string;
    column_metadata?: Record<string, any>;
    processing_time_ms?: number;
    error_message?: string;
  };
  progress: {
    total: number;
    processed: number;
    failed: number;
    percentage: number;
  };
  timestamp: string;
}

/**
 * Company-scope smart-column WS messages. These MIRROR the lead-scope
 * ``smart_column_update`` / ``smart_column_lead_update`` pair but are keyed
 * by ``company_id`` and carry a distinct ``type`` so the company handler and
 * the lead handler never consume each other's payloads.
 *
 * Per-row: ``smart_column_company_lead_update`` (the "lead" in the name is
 * the backend's generic per-row event name; the row here is a company).
 * Terminal: ``smart_column_company_update`` (no ``result`` — gate by task
 * ownership).
 */
export interface SmartColumnCompanyLeadUpdateMessage {
  type: 'smart_column_company_lead_update';
  task_id: string;
  company_id: string;
  column_type: string;
  column_name?: string | null;
  result: {
    id: string | null;
    company_id: string;
    lead_list_id: string;
    status: string;
    processed_value?: any;
    original_value?: any;
    column_metadata?: Record<string, any>;
    processing_time_ms?: number;
    error?: string | null;
  };
  progress: {
    total: number;
    processed: number;
    failed: number;
    percentage: number;
  };
  timestamp: string;
}

export interface SmartColumnCompanyUpdateMessage {
  type: 'smart_column_company_update';
  task_id: string;
  column_type: string;
  column_name?: string | null;
  status: string;
  progress: {
    total?: number;
    processed?: number;
    failed?: number;
    percentage?: number;
    error?: string;
  };
  timestamp: string;
}

export interface ProfileFillOutcomes {
  found: number;
  not_found: number;
  ambiguous: number;
  skipped: number;
  /** Reached, but the org could not pay for a hit. */
  unfunded: number;
  /** Never looked up: the balance ran out first or the run hit its deadline. */
  not_reached: number;
  credits_charged: number;
}

export interface EnrichmentMessage {
  type: 'enrichment_update';
  task_id?: string;
  enrichment_type?: 'partial' | 'full';
  workflow_id?: string;
  status?:
    | 'queued'
    | 'processing'
    | 'progress'
    | 'completed'
    | 'failed'
    | 'started'
    | 'session_completed'
    | 'prospecting'
    | 'prospecting_waiting'
    | 'prospecting_progress'
    | 'prospecting_completed'
    | 'enrichment_queuing'
    | 'enrichment_started'
    // Import-time schema enrichment converted a chunk of staged rows to
    // leads server-side (no per-row task exists in the browser); carries
    // lead_list_id + converted so the list view can repull its rows live.
    | 'schema_rows_converted'
    // The import-time profile fill is running. Carries lead_list_id + total +
    // processed + done so the list can show a bar for work that has no
    // per-row task in the browser, instead of looking idle until it lands.
    | 'profile_fill_progress';
  progress?: {
    total?: number;
    completed?: number;
    processed?: number;
    successful?: number;
    failed?: number;
    percentage?: number;
    index?: number;
  };
  prospecting_results?: {
    successful: number;
    failed: number;
    total: number;
  };
  pending_count?: number;
  completed_count?: number;
  total_tasks?: number;
  error?: string;
  prospect_id?: string;
  lead_id?: string; // Lead ID for tracking enrichment status per-row
  /** schema_rows_converted frames: which list's rows converted, so the list
   *  view can filter frames from other lists before repulling. */
  lead_list_id?: string;
  /** schema_rows_converted frames: how many staged rows became leads. */
  converted?: number;
  /** profile_fill_progress frames: rows finished, of how many, and whether
   *  the run is over (the client retires its bar on the terminal frame). */
  total?: number;
  processed?: number;
  done?: boolean;
  /** profile_fill_progress, terminal frame only: what the run did with the
   *  rows, for the list's closing message. */
  outcomes?: ProfileFillOutcomes;
  stage?: string;
  provider?: string;
  provider_index?: number;
  total_providers?: number;
  message?: string;
  credits_charged?: number;
  credits_refunded?: number;
  email_attempt_outcome?: EmailAttemptOutcome;
  result?: {
    email?: string;
    phone?: string;
    provider?: string;
    email_attempt_outcome?: EmailAttemptOutcome;
    /** How the backend graded the cascade — the same value the run banner
     *  tallies. Row text derives from this first so the two cannot disagree. */
    failure_kind?: EnrichmentFailureKind;
  };
  /** A refusal ended before any lookup (person_opted_out) sends its kind
   *  and `error` at the top level, with no `result`. */
  failure_kind?: EnrichmentFailureKind;
  summary?: {
    total: number;
    successful: number;
    failed: number;
  };
  // Prospect info included in queued messages for display
  first_name?: string;
  last_name?: string;
  linkedin_url?: string;
  company_domain?: string;
  timestamp: string;
}

export interface BulkProspectingMessage {
  type: 'bulk_prospecting_update';
  workflow_id: string;
  reveal_id: string;
  status: 'completed' | 'failed';
  prospect_id?: string;
  error?: string;
  timestamp: string;
}

export interface CrmLookupUpdateMessage {
  type: 'crm_lookup_update';
  task_id: string;
  column_type: 'crm_contact_lookup';
  status: string;
  progress: {
    total: number;
    processed: number;
    found: number;
    not_found: number;
    percentage: number;
  };
  timestamp: string;
}

export interface CrmLookupLeadUpdateMessage {
  type: 'crm_lookup_lead_update';
  task_id: string;
  lead_id: string;
  column_type: 'crm_contact_lookup';
  result: {
    id: string | null;
    lead_id: string;
    lead_list_id: string;
    processed_value: any;
    status: string;
    column_metadata?: Record<string, any>;
    processing_time_ms?: number;
    error_message?: string;
  };
  progress: {
    total: number;
    processed: number;
    failed: number;
    percentage: number;
  };
  timestamp: string;
}

export interface LeadCreatedMessage {
  type: 'lead_created';
  list_id: string;
  lead: any;
  total_count: number;
}

export interface LeadUpdatedMessage {
  type: 'lead_updated';
  list_id: string;
  lead_id: string;
  changes: Record<string, any>;
}

export interface LeadDeletedMessage {
  type: 'lead_deleted';
  list_id: string;
  lead_ids: string[];
  total_count: number;
}

export interface BulkOperationCompleteMessage {
  type: 'bulk_operation_complete';
  list_id: string;
  operation: 'import' | 'delete' | 'enrich';
  total_count: number;
}

export interface LeadListUpdateMessage {
  type: 'lead_list_update';
  // Back-compat singular id. Every historical emitter sends this; the handler
  // treats it as a 1-element affected_list_ids during the transition.
  lead_list_id?: string;
  // SSOT for "which list(s) did this op touch". Emitters that mutate more than
  // one list (move/copy => source + target) populate this. The FE handler
  // accepts BOTH shapes: plural here, or the singular lead_list_id above.
  affected_list_ids?: string[];
  action:
    | 'import_completed'
    | 'enrichment_completed'
    | 'updated'
    | 'import'
    | 'move'
    | 'copy'
    | 'delete'
    | 'create'
    | 'rename'
    | 'delete_list'
    | 'enrich'
    | 'run_search'
    | 'quick_action';
  timestamp: string;
}

export interface ActionColumnLeadUpdateMessage {
  type: 'action_column_lead_update';
  action_column_id: string;
  lead_id: string;
  result: {
    status: string;
    result?: Record<string, unknown>;
    error_message?: string;
  };
  progress: {
    total: number;
    processed: number;
    failed: number;
    percentage: number;
  };
  timestamp: string;
}

export interface ActionColumnProgressMessage {
  type: 'action_column_progress';
  action_column_id: string;
  lead_list_id: string;
  progress: {
    total: number;
    processed: number;
    failed: number;
    percentage: number;
  };
  timestamp: string;
}

export interface CreditUpdateMessage {
  type: 'credit_update';
  /** Optional during rollout: a backend that predates org-stamped credit
   *  broadcasts omits it, and those messages must still be honored. */
  organization_id?: string;
  count: number;
  /** What running Clu work holds; `count` is net of it. Absent from a
   *  backend that predates it, or when the server couldn't read it. */
  reserved?: number;
  email_credits: number;
  phone_credits: number;
  last_updated: string;
}

/** Per-organization presence roster pushed by cleanse-api core/presence.py.
 *  Carries user ids only — the portal joins them against the Clerk membership
 *  list it already loads, so no profile data is duplicated over the socket. */
export interface PresenceMessage {
  type: 'presence';
  organization_id: string;
  online: string[];
}

export interface OnboardingStepCompletedMessage {
  type: 'onboarding_step_completed';
  step: string;
  reward_earned: boolean;
  credits_awarded: number;
}

export interface FindPeopleProgressMessage {
  type: 'find_people_progress';
  payload: {
    job_id: string;
    lead_list_id: string;
    status: string;
    current_company?: string;
    processed_companies: number;
    total_companies: number;
    people_found: number;
    people_imported: number;
    progress_percent: number;
  };
}

export interface LeadListCompaniesUpdatedMessage {
  type: 'lead_list_companies_updated';
  payload: {
    lead_list_id: string;
    added: number;
    source: string;
  };
}

export interface PersonalEmailProgressMessage {
  type: 'personal_email_progress';
  payload: {
    job_id: string;
    lead_list_id: string;
    status: string;
    processed_leads: number;
    total_leads: number;
    found_count: number;
    progress_percent: number;
  };
}

export interface CompanyEnrichmentProgressMessage {
  type: 'company_enrichment_progress';
  payload: {
    job_id: string;
    lead_list_id: string;
    status: string;
    current_company?: string;
    provider?: string;
    processed_companies: number;
    total_companies: number;
    enriched_companies: number;
    credits_used: number;
    progress_percent: number;
    errors?: string[] | null;
  };
}

export type LeadsWebSocketMessage =
  | LeadCreatedMessage
  | LeadUpdatedMessage
  | LeadDeletedMessage
  | BulkOperationCompleteMessage
  | LeadListUpdateMessage
  | FindPeopleProgressMessage
  | LeadListCompaniesUpdatedMessage
  | PersonalEmailProgressMessage
  | CompanyEnrichmentProgressMessage;

/**
 * The server saved a message into a Clu conversation on its own (a slow
 * lookup's result): the open chat fetches it.
 */
export interface CopilotMessageAddedMessage {
  type: 'copilot_message_added';
  conversation_id: string;
  message_id: string;
  timestamp?: string;
}

/** Sent the moment a found contact is saved, before the workflow's billing /
 *  CRM tail. ``result`` is absent from workers older than the patch. */
export interface EnrichmentResultsReadyMessage {
  type: 'enrichment_results_ready';
  task_id?: string;
  lead_id?: string | null;
  status?: 'completed';
  result?: { email?: string; phone?: string };
}

export type WebSocketMessage =
  | SmartColumnMessage
  | SmartColumnLeadUpdateMessage
  | SmartColumnCompanyUpdateMessage
  | SmartColumnCompanyLeadUpdateMessage
  | EnrichmentMessage
  | EnrichmentResultsReadyMessage
  | BulkProspectingMessage
  | CrmLookupUpdateMessage
  | CrmLookupLeadUpdateMessage
  | ActionColumnLeadUpdateMessage
  | ActionColumnProgressMessage
  | CreditUpdateMessage
  | OnboardingStepCompletedMessage
  | PresenceMessage
  | CopilotMessageAddedMessage
  | LeadsWebSocketMessage;

export const isSmartColumnMessage = (
  message: any
): message is SmartColumnMessage => {
  return message?.type === 'smart_column_update';
};

export const isSmartColumnLeadUpdateMessage = (
  message: any
): message is SmartColumnLeadUpdateMessage => {
  return message?.type === 'smart_column_lead_update';
};

export const isSmartColumnCompanyUpdateMessage = (
  message: any
): message is SmartColumnCompanyUpdateMessage => {
  return message?.type === 'smart_column_company_update';
};

export const isSmartColumnCompanyLeadUpdateMessage = (
  message: any
): message is SmartColumnCompanyLeadUpdateMessage => {
  return message?.type === 'smart_column_company_lead_update';
};

export const isEnrichmentMessage = (
  message: any
): message is EnrichmentMessage => {
  return message?.type === 'enrichment_update';
};

export const isBulkProspectingMessage = (
  message: any
): message is BulkProspectingMessage => {
  return message?.type === 'bulk_prospecting_update';
};

export const isCrmLookupMessage = (
  message: any
): message is CrmLookupUpdateMessage => {
  return message?.type === 'crm_lookup_update';
};

export const isCrmLookupLeadUpdateMessage = (
  message: any
): message is CrmLookupLeadUpdateMessage => {
  return message?.type === 'crm_lookup_lead_update';
};

export const isLeadCreatedMessage = (
  message: any
): message is LeadCreatedMessage => {
  return message?.type === 'lead_created';
};

export const isLeadUpdatedMessage = (
  message: any
): message is LeadUpdatedMessage => {
  return message?.type === 'lead_updated';
};

export const isLeadDeletedMessage = (
  message: any
): message is LeadDeletedMessage => {
  return message?.type === 'lead_deleted';
};

export const isActionColumnLeadUpdateMessage = (
  message: any
): message is ActionColumnLeadUpdateMessage => {
  return message?.type === 'action_column_lead_update';
};

export const isActionColumnProgressMessage = (
  message: any
): message is ActionColumnProgressMessage => {
  return message?.type === 'action_column_progress';
};

export const isBulkOperationCompleteMessage = (
  message: any
): message is BulkOperationCompleteMessage => {
  return message?.type === 'bulk_operation_complete';
};

export const isCreditUpdateMessage = (
  message: any
): message is CreditUpdateMessage => {
  // organization_id is optional: requiring it here silently discards every
  // credit broadcast from a backend that hasn't started stamping it yet
  // (FE-before-BE deploy order), freezing balances until hard refresh. The
  // consumer filters on a present-and-mismatched id instead.
  //
  // `null` counts as absent, not as a bad value. An unstamped broadcast does
  // not always omit the key — a backend serializing the column straight to JSON
  // sends `organization_id: null`, which is neither undefined nor a string, so
  // treating only `undefined` as absent reintroduced the exact freeze this
  // guard exists to avoid.
  return (
    message?.type === 'credit_update' &&
    (message.organization_id === undefined ||
      message.organization_id === null ||
      typeof message.organization_id === 'string')
  );
};

export const isCopilotMessageAddedMessage = (
  message: any
): message is CopilotMessageAddedMessage => {
  return (
    message?.type === 'copilot_message_added' &&
    typeof message.conversation_id === 'string' &&
    typeof message.message_id === 'string'
  );
};

export const isLeadListUpdateMessage = (
  message: any
): message is LeadListUpdateMessage => {
  return message?.type === 'lead_list_update';
};

export const isFindPeopleProgressMessage = (
  message: any
): message is FindPeopleProgressMessage => {
  return message?.type === 'find_people_progress';
};

export const isLeadListCompaniesUpdatedMessage = (
  message: any
): message is LeadListCompaniesUpdatedMessage => {
  return message?.type === 'lead_list_companies_updated';
};

export const isPersonalEmailProgressMessage = (
  message: any
): message is PersonalEmailProgressMessage => {
  return message?.type === 'personal_email_progress';
};

export const isCompanyEnrichmentProgressMessage = (
  message: any
): message is CompanyEnrichmentProgressMessage => {
  return message?.type === 'company_enrichment_progress';
};
